/* eslint-disable camelcase */
import React, {
  KeyboardEvent,
  MouseEvent,
  useState,
  useRef,
  useEffect
} from 'react';
import { connect } from 'react-redux';

import CommentInputCoreContainer from 'annotator/comment-input-core-container';
import StudentCommentInput from 'annotator/student-comment-input';
import TutorCommentInput from 'annotator/tutor-comment-input';
import scrollToHighlight from 'annotator/scroll-to-highlight-helper';
import { snakeToPascalCase, commentHelperType, matchTimestamp } from './annotator-helpers';
import {
  Annotation,
  AnnotatorObject,
  CiteStatus,
  CommentCategory,
  CommentHelpersSummary,
  CommentInputAnnotation,
  CategoryTitle,
  UserTypes,
  DisplayType,
  CategoryClassName
} from './annotator-types';
import { BellmereQA } from './bellmere-qa/qa-types.d';

declare const Annotator: AnnotatorObject;

interface CommentInputProps {
  annotation: CommentInputAnnotation;
  bulkDeleteMode: boolean;
  commentCategory: CommentCategory;
  commentClass: string;
  commentHelpersSummary: CommentHelpersSummary;
  confirmingDeletion: boolean;
  contextWidth: null | number;
  inDiscussion?: boolean;
  isEditable: boolean;
  isEditing: boolean;
  isEditingDisabled: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  isVisible: boolean;
  markedForDeletion: boolean;
  onAssignCommentVariants: (
    fromTimestamp: string,
    newCommentHelperId: number,
    callback?: (newComment?: string) => void
  ) => void;
  onCancelEditingAnnotation: () => void;
  onCancelRemovingAnnotation: () => void;
  onEditComment: (timestamp: string) => void;
  onMarkedForDeletionChange: () => void;
  onRemoveAnnotation: (id: number, timestamp: string, reason: string, callback?: () => void) => void;
  onRestoreAnnotation: () => void;
  onSaveAnnotation: (data: Partial<Annotation>, callback?: () => void) => void;
  onStartRemovingAnnotation: (fromTimestamp: string) => void;
  onToggleAnnotationCompletion: (annotation: Pick<Annotation, 'timestamp' | 'uuid' | 'completed_at'>) => void;
  completionPending: boolean;
  restorable: boolean;
  savingSubmission: boolean;
  selectedParagraphIndex: string | null;
  viewingAsBellmereQa: boolean,
  setBellmereQAResult: (result: BellmereQA.AnnotationResult) => void,
  bellmereQAResults: BellmereQA.AnnotationResult[],
  bellmereQAEditable: boolean
}

const MAX_DISPLAY_LENGTH = 200; // NOTE: old name was `maxDisplayLength`

const CommentInput = ({
  annotation,
  bulkDeleteMode,
  commentCategory,
  commentClass,
  commentHelpersSummary,
  confirmingDeletion,
  contextWidth,
  inDiscussion,
  isEditable,
  isEditing,
  isEditingDisabled,
  isHighlighted,
  isSelected,
  isVisible,
  markedForDeletion,
  onAssignCommentVariants,
  onCancelEditingAnnotation,
  onCancelRemovingAnnotation,
  onEditComment,
  onMarkedForDeletionChange,
  onRemoveAnnotation,
  onRestoreAnnotation,
  onSaveAnnotation,
  onStartRemovingAnnotation,
  onToggleAnnotationCompletion,
  completionPending,
  restorable,
  savingSubmission,
  selectedParagraphIndex,
  viewingAsBellmereQa,
  bellmereQAResults,
  setBellmereQAResult,
  bellmereQAEditable
}: CommentInputProps) => {
  const [deleting, setDeleting] = useState(false);
  const [expandedAnnotation] = useState(false);
  const isDone = !!annotation.completed_at;

  const isMounted = useRef(false);
  const node = useRef<HTMLDivElement>();

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ---------- BEGIN HYBRID FUNCTIONS (BOTH EFFECT AND HELPER FUNCTIONS)

  const editComment = (): void => {
    const { timestamp } = annotation;

    onEditComment(timestamp);
  };

  const saveComment = (comment: string, commentHelperId: number): void => {
    const { id, timestamp, additionalInfo } = annotation;

    if (commentHelperId) {
      const idToAdd = parseInt(String(commentHelperId), 10);
      const commentHelper = Annotator.data.commentHelpers.find((ch) => ch.id === idToAdd);

      if (commentHelper) {
        Annotator.addCommentHelperToFeedbackSummary(commentHelper);
      }
    }

    onSaveAnnotation(
      {
        id,
        comment,
        timestamp,
        additional_info: additionalInfo,
        submission_uuid: Annotator.data.submissionUUID,
        comment_helper_ids: [commentHelperId]
      }
    );
  };

  // ---------- BEGIN HELPER FUNCTIONS

  const annotationText = (): string => {
    const { highlightsText, sentences } = annotation;

    // For multi-paragraph annotations, return a placeholder that will be handled differently in the UI
    if (sentences && sentences.length > 1) {
      return null; // Will be handled by sentences rendering
    }

    if (!expandedAnnotation && highlightsText.length > MAX_DISPLAY_LENGTH) {
      return `${highlightsText.substr(0, MAX_DISPLAY_LENGTH)}...`;
    }

    if (highlightsText.trim().length === 0) {
      return '<space>';
    }

    return highlightsText;
  };

  const assignCommentVariants = (newCommentHelperId: number, callback: (newComment: string) => void): void => {
    const { timestamp } = annotation;

    onAssignCommentVariants(timestamp, newCommentHelperId, callback);
  };

  const removeAnnotation = (e?: MouseEvent, reason?: string): void => {
    const { id, timestamp } = annotation;

    if (deleting) {
      return;
    }

    setDeleting(true);
    e && e.stopPropagation(); // eslint-disable-line no-unused-expressions

    onRemoveAnnotation(id, timestamp, reason, () => {
      setDeleting(false);
    });
  };

  const cancelEdit = (): void => {
    const { id } = annotation;

    onCancelEditingAnnotation();

    if (!id) {
      removeAnnotation();
    }
  };

  const removeCitation = (reason: string): void => {
    removeAnnotation(null, reason);
  };

  const saveCiteStatus = (status: CiteStatus): void => {
    const { id, timestamp } = annotation;
    onSaveAnnotation({ id, timestamp, cite_status: status });
  };

  const wrapperClass = (): string => {
    const { sources } = annotation;

    return [
      'panel panel-default panel-comment m-1',
      !isVisible && 'hidden',
      sources && 'panel-citation',
      isEditing && 'panel-comment-edit',
      !isEditing && isSelected && isHighlighted && 'panel-comment-selected',
      !isEditing && isSelected && 'panel-comment-expanded',
      inDiscussion && 'panel-comment-in-discussion',
      restorable && 'panel-comment-restorable',
      commentClass,
      Annotator.userType,
      Annotator.portFairyEnabled && 'new'
    ].filter(Boolean).join(' ');
  };

  const selectHighlight = (): void => {
    const { timestamp, display_type, paragraph_indices } = annotation;

    // Clicking a card that's already selected and highlighted closes it instead of re-selecting it.
    if (isSelected && isHighlighted) {
      Annotator.deselectHighlights();
      return;
    }

    // For multi-paragraph annotations, select only the first paragraph
    const firstParagraphIndex = paragraph_indices && paragraph_indices.length > 1
      ? paragraph_indices[0]?.toString()
      : null;

    // Select the highlight.
    Annotator.selectHighlight(
      display_type === DisplayType.Paragraph ?
        `${timestamp},${CategoryClassName[snakeToPascalCase(commentCategory)]}` : timestamp,
      true,
      firstParagraphIndex
    );
    // Scroll to the highlight (if necessary).
    scrollToHighlight(matchTimestamp(timestamp));
  };

  const keyboardSelectHighlight = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectHighlight();
    }
  };

  const hoverHighlight = (e: MouseEvent): void => {
    const { timestamp, display_type, sentences } = annotation;
    e.preventDefault();

    document.querySelector('.annotator-submission-doc-wrap .panel-body .highlighted.hover')?.classList?.remove('hover');
    document.querySelectorAll('.annotator-submission-doc-wrap .panel-body .paragraph-highlight.hover').forEach(
      (el) => el.classList?.remove('hover')
    );

    // For multi-paragraph annotations, highlight all paragraphs
    if (sentences && sentences.length > 1) {
      // Find all icons that start with the annotation timestamp
      const allIcons = Array.from(document.querySelectorAll('.paragraph-icon')).filter(
        (icon) => icon.id.startsWith(annotation.timestamp)
      );

      // Use the icons we found instead of trying to construct IDs
      allIcons.forEach((icon) => {
        const highlight = icon.closest('.paragraph-highlight') as HTMLElement;
        if (highlight) {
          highlight.classList.add('hover');
          if (display_type === DisplayType.Paragraph) {
            highlight.dataset.commentCategory = commentClass;
            icon.classList.add('selected');
          }
        }
      });
    } else {
      let highlight: HTMLElement | null = null;

      if (display_type === DisplayType.Paragraph) {
        // For paragraph annotations, find the paragraph by locating the icon first
        // This works even when paragraphs have overlapping annotations
        const icon = document.getElementById(annotation.timestamp);
        if (icon) {
          // Navigate up from icon to find the paragraph element
          highlight = icon.closest('.paragraph-highlight') as HTMLElement;
        }
      } else {
        // For sentence annotations, use the matchTimestamp approach
        const matchedTs = matchTimestamp(timestamp);
        highlight = document.querySelector(
          `.annotator-submission-doc-wrap [data-timestamp='${matchedTs}']`
        ) as HTMLElement;
      }

      highlight?.classList?.add('hover');

      if (highlight && display_type === DisplayType.Paragraph) {
        highlight.dataset.commentCategory = commentClass;
        const icon = document.getElementById(annotation.timestamp);
        icon?.classList.add('selected');
      }
    }
  };

  const dehoverHighlights = (e: MouseEvent): void => {
    const { timestamp, display_type, sentences } = annotation;
    e.preventDefault();

    document.querySelector('.annotator-submission-doc-wrap .panel-body .highlighted.hover')?.classList?.remove('hover');

    if (display_type === DisplayType.Paragraph) {
      const firstHighlight =
        document.querySelectorAll(`.panel-comment[data-timestamp='${timestamp}']`)[0];

      // For multi-paragraph annotations, remove hover from all paragraphs
      if (sentences && sentences.length > 1) {
        const highlights = document.querySelectorAll(
          `.annotator-submission-doc-wrap [data-timestamp='${matchTimestamp(timestamp)}']`
        );
        if (firstHighlight) {
          const categoryClass = Array.from(firstHighlight.classList).find((c) => c.includes('comment-category'));
          highlights.forEach((highlight: HTMLElement) => {
            const element = highlight;
            element.dataset.commentCategory = categoryClass;
            // For multi-paragraph, remove selected from icon with paragraph index suffix
            if (!isSelected) {
              const iconId = `${annotation.timestamp}-${element.dataset.paragraphIndex}`;
              document.getElementById(iconId)?.classList.remove('selected');
            }
          });
        }
      } else {
        const highlight = (
          document.querySelector(
            `.annotator-submission-doc-wrap [data-timestamp='${matchTimestamp(timestamp)}']`
          ) as HTMLElement
        );
        if (highlight && firstHighlight) {
          highlight.dataset.commentCategory =
            Array.from(firstHighlight.classList).find((c) => c.includes('comment-category'));
        }
        if (!isSelected) document.getElementById(annotation.timestamp)?.classList.remove('selected');
      }
    }
  };

  // ---------- BEGIN RENDER

  const {
    comment,
    sources,
    timestamp
  } = annotation;

  const hasSources = !!sources;
  const showSourcesInfoTooltip = !isEditable && hasSources && comment;

  const commentIcon = commentClass ? `icon-${commentClass}` : 'icon-comment-category-general';
  const commentClasses = `icon icon-sm mb-0 ${commentIcon}`;

  const handleRestoreAnnotation = () => {
    onRestoreAnnotation();
    setDeleting(false);
  };

  const capitalizedCommentCategory = CategoryTitle[snakeToPascalCase(commentCategory)];
  const commentType = commentHelperType(annotation);
  const commentCategoryAndType: string = [capitalizedCommentCategory, commentType].filter(Boolean).join(' • ');

  if (!isVisible) {
    return null;
  }

  const commentInputCoreContainer = (
    <CommentInputCoreContainer
      annotation={annotation}
      annotationText={annotationText()}
      assignCommentVariants={assignCommentVariants}
      bulkDeleteMode={bulkDeleteMode}
      cancelEdit={cancelEdit}
      commentCategory={commentCategory}
      commentCategoryAndType={commentCategoryAndType}
      commentType={commentType}
      commentIcon={commentIcon}
      capitalizedCommentCategory={capitalizedCommentCategory}
      commentHelpersSummary={commentHelpersSummary}
      confirmingDeletion={confirmingDeletion}
      contextWidth={contextWidth}
      editComment={editComment}
      handleRestoreAnnotation={handleRestoreAnnotation}
      hasSources={hasSources}
      inDiscussion={inDiscussion}
      isEditable={isEditable}
      isEditing={isEditing}
      isEditingDisabled={isEditingDisabled || savingSubmission}
      isPanelSelected={isSelected}
      markedForDeletion={markedForDeletion}
      onCancelRemovingAnnotation={onCancelRemovingAnnotation}
      onMarkedForDeletionChange={onMarkedForDeletionChange}
      onStartRemovingAnnotation={onStartRemovingAnnotation}
      keyboardSelectHighlight={keyboardSelectHighlight}
      selectHighlight={selectHighlight}
      removeAnnotation={removeAnnotation}
      removeCitation={removeCitation}
      restorable={restorable}
      saveCiteStatus={saveCiteStatus}
      saveComment={saveComment}
      savingSubmission={savingSubmission}
      selectedParagraphIndex={selectedParagraphIndex}
      showCollapsed={Annotator.userType === UserTypes.Student}
      viewingAsBellmereQa={viewingAsBellmereQa}
      bellmereQAResults={bellmereQAResults}
      setBellmereQAResult={setBellmereQAResult}
      bellmereQAEditable={bellmereQAEditable}
      isDone={isDone}
      onToggleCompletion={() => onToggleAnnotationCompletion(annotation)}
      completionPending={completionPending}
    />
  );

  switch (Annotator.userType) {
    case UserTypes.Student:
      return (
        <StudentCommentInput
          node={node}
          wrapperClass={wrapperClass()}
          timestamp={timestamp}
          selectHighlight={selectHighlight}
          keyboardSelectHighlight={keyboardSelectHighlight}
          hoverHighlight={hoverHighlight}
          dehoverHighlights={dehoverHighlights}
          sources={sources}
          comment={comment}
          commentClasses={commentClasses}
          commentIcon={commentIcon}
          showSourcesInfoTooltip={showSourcesInfoTooltip}
          commentType={commentType}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
        >
          {commentInputCoreContainer}
        </StudentCommentInput>
      );
    case UserTypes.Tutor:
      return (
        <TutorCommentInput
          node={node}
          wrapperClass={wrapperClass()}
          timestamp={timestamp}
          selectHighlight={selectHighlight}
          keyboardSelectHighlight={keyboardSelectHighlight}
          hoverHighlight={hoverHighlight}
          dehoverHighlights={dehoverHighlights}
          sources={sources}
          comment={comment}
          commentClasses={commentClasses}
          commentIcon={commentIcon}
          showSourcesInfoTooltip={showSourcesInfoTooltip}
          commentType={commentType}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
        >
          {commentInputCoreContainer}
        </TutorCommentInput>
      );
    default:
      return null;
  }
};

const mapStateToProps = (state, props) => ({
  inDiscussion: state.lastHighlightAdded === props.annotation.timestamp
});

export default CommentInput;
export const YambaCommentInput = connect(mapStateToProps)(CommentInput);
